import { AxiosInstance } from "axios";
import moment from "moment";
import { db } from "@/utils/dexie";
import { processResult } from "@/utils/processResult";
import { getDatesFromStartEnd, groupDateRanges } from "@/utils/settlementUtils";
import { NonPIIOrder, NonPIIRefund } from "@/types/settlement";
import {
  fetchOrdersByDateRange,
  fetchRefundsByDateRange,
  fetchOrdersByIdsChunked,
} from "./settlementApi";
import {
  saveOrderAndRefund,
  saveOrderReferences,
  getOrdersByIdsFromDb,
  saveResult,
  getResultsFromDb,
} from "./settlementCache";
import { sanitizeOrderNonPII } from "./dataTransformer";
import {
  saveSettlementTime,
  getSettlementTime,
} from "@/utils/settlementTimeTracker";

export {
  getResultsFromDb,
  saveResult,
  saveOrderAndRefund,
  saveOrderReferences,
  getOrdersByIdsFromDb,
};
export { sanitizeOrderNonPII };

export interface SettlementServiceOptions {
  axios: AxiosInstance;
  mallId: string;
  shopNo: string | number;
  startDate: string;
  endDate: string;
  refresh?: boolean;
  signal?: AbortSignal;
  onProgress?: (message: string, mode?: "refresh" | "settlement") => void;
  onNotice?: (message: string) => void;
}

export interface FetchSettlementResult {
  processedResult: any;
  missingDates: string[];
  mergedOrders: NonPIIOrder[];
  mergedRefunds: NonPIIRefund[];
}

/**
 * Main Orchestration function to fetch and calculate settlement data
 */
export async function fetchSettlementData(
  options: SettlementServiceOptions
): Promise<FetchSettlementResult> {
  const {
    axios,
    mallId,
    shopNo,
    startDate,
    endDate: initialEndDate,
    refresh = false,
    signal,
    onProgress,
    onNotice,
  } = options;

  const throwIfAborted = () => {
    if (signal?.aborted) {
      throw new Error("Settlement query aborted");
    }
  };

  const today = moment().format("YYYY-MM-DD");
  const endDate = initialEndDate > today ? today : initialEndDate;
  const strShopNo = String(shopNo);

  const mergedOrders: any[] = [];
  const mergedRefunds: any[] = [];
  const missingDates: string[] = [];
  const cachedTimestamps: number[] = [];

  // Step 1: Check Dexie cache or collect missing dates
  for (const date of getDatesFromStartEnd(startDate, endDate)) {
    throwIfAborted();
    if (refresh || date === today) {
      missingDates.push(date);
    } else {
      const row = await db.results
        .where("[mall_id+shop_no+date]")
        .equals([mallId, strShopNo, date])
        .first();

      if (row) {
        if (typeof row.updated_at === "number" && row.updated_at > 0) {
          cachedTimestamps.push(row.updated_at);
        }
        mergedOrders.push(...row.orders);
        mergedRefunds.push(...row.refunds);
      } else {
        missingDates.push(date);
      }
    }
  }

  // Step 2: Fetch missing data from Cloudflare API in date ranges
  for (const range of groupDateRanges(missingDates)) {
    throwIfAborted();
    let dateString = "";
    if (range.start === range.end) {
      dateString = `${moment(range.start).format("YYYY년 M월 D일")}<br>`;
    } else {
      dateString = `${moment(range.start).format(
        "YYYY년 M월 D일"
      )} 부터 ${moment(range.end).format("YYYY년 M월 D일")} 까지<br>`;
    }

    if (onProgress) {
      onProgress(
        `${dateString}Cafe24에서 결제내역을 가져오는 중...`,
        "refresh"
      );
    }

    // Step 2a: Fetch orders for the requested payment-date range and refunds
    // for the requested refund-date range. These are the only records that may
    // participate in the date-range settlement aggregate.
    const periodOrders = await fetchOrdersByDateRange(
      axios,
      range.start,
      range.end,
      signal
    );
    const refunds = await fetchRefundsByDateRange(
      axios,
      range.start,
      range.end,
      signal
    );

    // Step 2b: Find missing order IDs referenced by refunds
    const orderMap = new Map<string, any>();
    periodOrders.forEach((order: any) => {
      const orderId = String(order?.order_id || "").trim();
      if (orderId) orderMap.set(orderId, order);
    });
    const notFoundOrderIds = Array.from(
      new Set(
        refunds
          .map((refund: any) => String(refund?.order_id || "").trim())
          .filter((orderId: string) => orderId && !orderMap.has(orderId))
      )
    );
    let referenceOrders: any[] = [];

    // Step 2c: Fetch missing orders sequentially
    if (notFoundOrderIds.length > 0) {
      const cachedReferenceOrders = await getOrdersByIdsFromDb(
        mallId,
        strShopNo,
        notFoundOrderIds
      );
      const cachedReferenceOrderIds = new Set(
        cachedReferenceOrders.map((o: any) => o.order_id)
      );
      const missingReferenceOrderIds = notFoundOrderIds.filter(
        (orderId) => !cachedReferenceOrderIds.has(orderId)
      );

      referenceOrders = [...cachedReferenceOrders];
      if (missingReferenceOrderIds.length > 0) {
        const fetchedReferenceOrders = await fetchOrdersByIdsChunked(
          axios,
          missingReferenceOrderIds,
          50,
          600,
          signal
        );
        referenceOrders.push(...fetchedReferenceOrders);
      }

      referenceOrders.forEach((o: any) => {
        const orderId = String(o?.order_id || "").trim();
        if (orderId) orderMap.set(orderId, o);
      });
    }

    // Step 2d: Link Orders to Refunds
    refunds.forEach((r: any) => {
      const orderId = String(r?.order_id || "").trim();
      if (orderId && orderMap.has(orderId)) {
        r.order = orderMap.get(orderId);
      }
    });

    // Step 2e: Sanitize order payload; preserve the full refund payload.
    const filteredOrders = periodOrders.map((o: any) => sanitizeOrderNonPII(o));
    const filteredReferenceOrders = referenceOrders.map((o: any) =>
      sanitizeOrderNonPII(o)
    );
    const filteredRefunds = refunds;

    if (today >= range.start && today <= range.end && onNotice) {
      onNotice("오늘 주문 정보는 캐싱하지 않습니다.");
    }

    const saveDates = Array.from(
      { length: moment(range.end).diff(moment(range.start), "days") + 1 },
      (_, i) => moment(range.start).clone().add(i, "days").format("YYYY-MM-DD")
    );

    const resultPayload = {
      mall_id: mallId,
      shop_no: strShopNo,
      orders: filteredOrders,
      refunds: filteredRefunds,
    };

    await Promise.all([
      saveResult(resultPayload, saveDates),
      saveOrderReferences(mallId, strShopNo, [
        ...filteredOrders,
        ...filteredReferenceOrders,
      ]),
    ]);

    mergedOrders.push(...filteredOrders);
    mergedRefunds.push(...filteredRefunds);
  }

  if (onProgress) {
    throwIfAborted();
    onProgress("정산 계산 중...", "settlement");
  }

  const isActualSettlementPerformed = missingDates.length > 0;
  let settledTimestamp: number | undefined;

  if (isActualSettlementPerformed) {
    settledTimestamp = Date.now();
    saveSettlementTime(mallId, strShopNo, startDate, endDate, settledTimestamp);
  } else {
    settledTimestamp =
      cachedTimestamps.length > 0
        ? Math.max(...cachedTimestamps)
        : getSettlementTime(mallId, strShopNo, startDate, endDate) ?? undefined;
  }

  const processedResult = await processResult({
    mall_id: mallId,
    shop_no: strShopNo,
    start_date: startDate,
    end_date: endDate,
    orders: mergedOrders,
    refunds: mergedRefunds,
    updated_at: settledTimestamp,
  });

  return {
    processedResult,
    missingDates,
    mergedOrders,
    mergedRefunds,
  };
}
